<script setup>
import { computed, onMounted, ref } from 'vue';
import LogoFromDomain from '../../../components/crm/LogoFromDomain.vue';
import { _mft } from '../../../i18n';

const props = defineProps({
    leadCompanyId: {
        type: Number,
        required: true,
    },
});

const leadCompany = ref(null);
const loading = ref(true);
const workingState = ref('');
const addingContact = ref(false);
const showInsights = ref(false);
const disconnectingLeadId = ref(null);
const deleteDialogOpen = ref(false);
const deleteState = ref('');
const deleteError = ref('');
const errors = ref({});

const DESCRIPTION_MAX_LENGTH = 5000;
const DESCRIPTION_COUNTER_FROM = 4500;

const descriptionLength = computed(
    () => leadCompany.value?.description?.length ?? 0
);

const showDescriptionCounter = computed(
    () => descriptionLength.value >= DESCRIPTION_COUNTER_FROM
);

const DESCRIPTION_ANNOUNCE_AT = [4500, 4900, 5000];

// Changes only when a threshold is crossed, so the live region does not announce every keystroke.
const descriptionAnnouncement = computed(() => {
    const threshold = [...DESCRIPTION_ANNOUNCE_AT]
        .reverse()
        .find((at) => descriptionLength.value >= at);

    return threshold === undefined
        ? ''
        : _mft('crm:contact.company.fields.descriptionCharactersLeft', {
              count: DESCRIPTION_MAX_LENGTH - threshold,
          });
});

const primaryLeadOptions = computed(() => {
    const options = [
        {
            value: null,
            label: _mft('crm:leadCompany.noPrimaryContact'),
        },
    ];

    for (const lead of leadCompany.value?.leads ?? []) {
        options.push({
            value: lead.id,
            label: [lead.name, lead.email].filter(Boolean).join(' • '),
        });
    }

    return options;
});

const loadLeadCompany = async () => {
    loading.value = true;

    try {
        const res = await axios.get(
            route('bo.api.v1.crm.lead-company.single', props.leadCompanyId)
        );
        leadCompany.value = res.data.lead_company;
    } finally {
        loading.value = false;
    }
};

const updateLeadCompany = async () => {
    if (!leadCompany.value) {
        return;
    }

    workingState.value = 'working';
    errors.value = {};

    try {
        const payload = {
            name: leadCompany.value.name,
            website: leadCompany.value.website,
            org_nr: leadCompany.value.org_nr,
            invoice_email: leadCompany.value.invoice_email,
            employees: leadCompany.value.employees,
            billing_street_address: leadCompany.value.billing_street_address,
            billing_city: leadCompany.value.billing_city,
            billing_zipcode: leadCompany.value.billing_zipcode,
            billing_country: leadCompany.value.billing_country,
            description: leadCompany.value.description || null,
            primary_lead_id:
                leadCompany.value.primary_lead_id === '' ||
                leadCompany.value.primary_lead_id === null
                    ? null
                    : Number(leadCompany.value.primary_lead_id),
        };

        const res = await axios.put(
            route('bo.api.v1.crm.lead-company.update', props.leadCompanyId),
            payload
        );
        leadCompany.value = res.data.lead_company;
        workingState.value = 'success';
    } catch (error) {
        errors.value = error.response?.data?.errors ?? {};
        workingState.value = 'error';
    }
};

const handleLeadCreated = async () => {
    addingContact.value = false;
    await loadLeadCompany();
};

const openDeleteDialog = () => {
    if (deleteState.value !== 'waiting') {
        deleteError.value = '';
        deleteState.value = '';
    }
    deleteDialogOpen.value = true;
};

const closeDeleteDialog = () => {
    deleteDialogOpen.value = false;
};

const deleteLeadCompany = async () => {
    if (deleteState.value === 'waiting') {
        return;
    }

    deleteState.value = 'waiting';
    deleteError.value = '';

    try {
        await axios.delete(
            route('bo.api.v1.crm.lead-company.destroy', props.leadCompanyId)
        );
        window.location.assign(route('crm.lead-company.index'));
    } catch (error) {
        deleteState.value = 'error';
        deleteError.value = _mft('error:error.generalTryAgainLater');
        deleteDialogOpen.value = true;
    }
};

const disconnectLead = async (lead) => {
    if (!leadCompany.value || disconnectingLeadId.value !== null) {
        return;
    }

    disconnectingLeadId.value = lead.id;

    try {
        const res = await axios.delete(
            route('bo.api.v1.crm.lead-company.lead.disconnect', {
                id: props.leadCompanyId,
                leadId: lead.id,
            })
        );
        leadCompany.value = res.data.lead_company;
    } finally {
        disconnectingLeadId.value = null;
    }
};

const disconnectButtonState = (lead) => {
    return disconnectingLeadId.value === lead.id ? 'waiting' : '';
};

const noteEndpoints = {
    index: route('bo.api.v1.crm.lead-company.notes.index', props.leadCompanyId),
    store: route('bo.api.v1.crm.lead-company.notes.store', props.leadCompanyId),
    update: (noteId) => route('bo.api.v1.crm.lead-company.notes.update', {id: props.leadCompanyId, noteId}),
    destroy: (noteId) => route('bo.api.v1.crm.lead-company.notes.destroy', {id: props.leadCompanyId, noteId}),
};

const insightsUrl = computed(() => {
    return leadCompany.value?.website || null;
});

const insightsEmail = computed(() => {
    return (
        leadCompany.value?.primary_lead?.email ||
        leadCompany.value?.invoice_email ||
        null
    );
});

const canShowInsightsButton = computed(() => {
    return Boolean(insightsUrl.value || insightsEmail.value);
});

const hasCloserHired = computed(() => {
    return window.myflow?.company?.hired_specialists?.includes('nina') === true;
});

const closerUnavailableTooltip = computed(() => {
    return hasCloserHired.value ? null : _mft('crm:nina.requiresCloserHired');
});

const toggleCloserReport = () => {
    if (!hasCloserHired.value || !canShowInsightsButton.value) {
        return;
    }

    showInsights.value = !showInsights.value;
};

onMounted(() => {
    loadLeadCompany();
});
</script>

<template>
    <div v-if="loading" class="py-8">
        <mf-spinner-medium class="w-24 h-12" />
    </div>

    <div v-else-if="leadCompany">
        <div class="md:sticky top-2 z-topbar mb-8">
            <frosted-bar>
                <template #navigation>
                    <a
                        :href="route('crm.lead-company.index')"
                        class="mf-link text-xs"
                    >
                        {{ _mft('crm:leadCompany.allCompanies') }}
                    </a>
                </template>

                <div class="flex items-center justify-between space-x-4">
                    <div class="flex items-center space-x-2">
                        <LogoFromDomain
                            :website="leadCompany.website"
                            :email="leadCompany.primary_lead?.email"
                            :company-name="leadCompany.name"
                            class="object-contain w-8 h-8 shrink-0"
                        />
                        <div>
                            <h1>{{ leadCompany.name }}</h1>
                            <p class="text-xxs text-gray-500 !mb-0">
                                {{
                                    _mft(
                                        'crm:leadCompany.linkedContactsCount',
                                        { count: leadCompany.leads_count ?? 0 }
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <div class="flex items-center space-x-2">
                        <button-more-options
                            alignment="right"
                            options-class="min-w-44"
                            :cancel-on-outside-click="false"
                        >
                            <button-more-options-option
                                style-type="danger"
                                @click="openDeleteDialog"
                            >
                                {{ _mft('crm:leadCompany.deleteCompany') }}
                            </button-more-options-option>
                        </button-more-options>

                        <button-secondary @click="addingContact = true">
                            {{ _mft('crm:addContact') }}
                        </button-secondary>

                        <container-with-tooltip
                            v-if="canShowInsightsButton || !hasCloserHired"
                            :class="{ 'cursor-not-allowed': !hasCloserHired }"
                            additional-tooltip-class="whitespace-nowrap"
                            alignment="right"
                            theme="dark"
                        >
                            <button-base
                                :style-type="
                                    hasCloserHired ? 'secondary' : 'disabled'
                                "
                                :disabled="!hasCloserHired"
                                @click="toggleCloserReport"
                            >
                                <template #icon>
                                    <i
                                        class="scale-125 fa-regular fa-head-side-brain"
                                    ></i>
                                </template>
                                <span class="hidden md:block ml-1.5">{{
                                    _mft('crm:nina.closerReport')
                                }}</span>
                            </button-base>

                            <template v-if="!hasCloserHired" #tooltip>
                                <div>
                                    {{ closerUnavailableTooltip }}
                                </div>
                            </template>
                        </container-with-tooltip>

                        <button-success
                            @click="updateLeadCompany"
                            :state="workingState"
                        >
                            {{ _mft('shared:action.update') }}
                        </button-success>

                        <div class="absolute">
                            <add-insights-domain-with-parser
                                v-if="showInsights && canShowInsightsButton"
                                :url="insightsUrl"
                                :email="insightsEmail"
                                @close="showInsights = false"
                            ></add-insights-domain-with-parser>
                        </div>
                    </div>
                </div>
            </frosted-bar>
        </div>

        <div class="grid gap-8 lg:grid-cols-3">
            <div class="col-span-3 lg:col-span-2">
                <card-basic>
                    <div class="@container">
                        <div class="grid-cols-4 gap-x-8 md:grid @sm:grid-cols-2 @lg:grid-cols-4">
                            <div class="col-span-2">
                                <input-text
                                    maxlength="255"
                                    input-name="lead_company_name"
                                    input-id="lead_company_name"
                                    :label="_mft('crm:contact.company.fields.name')"
                                    v-model:content="leadCompany.name"
                                />

                                <input-text
                                    maxlength="255"
                                    input-name="lead_company_org_nr"
                                    input-id="lead_company_org_nr"
                                    :label="_mft('crm:contact.company.fields.corporateIdentityNumber')"
                                    v-model:content="leadCompany.org_nr"
                                />

                                <div class="flex items-center space-x-2">
                                    <input-text
                                        maxlength="255"
                                        input-name="lead_company_website"
                                        input-id="lead_company_website"
                                        class="w-full"
                                        :label="_mft('crm:contact.company.fields.website')"
                                        v-model:content="leadCompany.website"
                                    />
                                    <a
                                        v-if="leadCompany.website?.length > 11"
                                        :href="leadCompany.website"
                                        target="_blank"
                                    >
                                        <i class="w-6 h-6 mt-2 text-gray-400 fa-light fa-globe"></i>
                                    </a>
                                </div>

                                <div class="flex items-center space-x-2">
                                    <input-text
                                        maxlength="255"
                                        input-name="lead_company_invoice_email"
                                        input-id="lead_company_invoice_email"
                                        class="w-full"
                                        :label="_mft('crm:contact.company.fields.invoiceEmail')"
                                        v-model:content="leadCompany.invoice_email"
                                    />
                                </div>

                                <input-number
                                    input-name="lead_company_employees"
                                    input-id="lead_company_employees"
                                    v-model:content="leadCompany.employees"
                                    :label="_mft('crm:contact.company.fields.employees')"
                                />

                                <input-select
                                    input-name="lead_company_primary_lead"
                                    input-id="lead_company_primary_lead"
                                    :label="_mft('crm:leadCompany.primaryContact')"
                                    v-model:content="leadCompany.primary_lead_id"
                                    :options="primaryLeadOptions"
                                >
                                    <option value="">{{ _mft('crm:leadCompany.noPrimaryContact') }}</option>
                                    <option
                                        v-for="lead in leadCompany.leads"
                                        :key="lead.id"
                                        :value="lead.id"
                                    >
                                        {{ [lead.name, lead.email].filter(Boolean).join(' • ') }}
                                    </option>
                                </input-select>
                            </div>

                            <div class="col-span-2">
                                <div class="">
                                    <input-text
                                        maxlength="255"
                                        input-name="lead_company_billing_street_address"
                                        input-id="lead_company_billing_street_address"
                                        :label="_mft('crm:contact.company.fields.billingStreetAddress')"
                                        v-model:content="leadCompany.billing_street_address"
                                    />

                                    <input-text
                                        maxlength="255"
                                        input-name="lead_company_billing_zipcode"
                                        input-id="lead_company_billing_zipcode"
                                        :label="_mft('crm:contact.company.fields.billingZipcode')"
                                        v-model:content="leadCompany.billing_zipcode"
                                    />
                                </div>

                                <div class="">
                                    <input-text
                                        maxlength="255"
                                        input-name="lead_company_billing_city"
                                        input-id="lead_company_billing_city"
                                        :label="_mft('crm:contact.company.fields.billingCity')"
                                        v-model:content="leadCompany.billing_city"
                                    />

                                    <input-language
                                        :label="_mft('crm:contact.company.fields.billingCountry')"
                                        name="lead_company_country"
                                        v-model:country="leadCompany.billing_country"
                                        @selected="leadCompany.billing_country = $event.label"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </card-basic>

                <div class="mt-4">
                    <lead-note-card :endpoints="noteEndpoints" />
                </div>
            </div>

            <div class="col-span-3 lg:col-span-1">
                <div class="mb-4 flex items-center justify-between gap-4">
                    <h3 class="mb-0">
                        {{ _mft('crm:leadCompany.linkedContacts') }}
                    </h3>
                </div>

                <div
                    v-if="(leadCompany.leads?.length ?? 0) === 0"
                    class="text-sm text-gray-500"
                >
                    {{ _mft('crm:leadCompany.noLinkedContacts') }}
                </div>

                <div v-else class="space-y-3">
                    <card-sub v-for="lead in leadCompany.leads" :key="lead.id">
                        <div
                            class="group flex items-center justify-between gap-3 relative"
                        >
                            <a
                                :href="route('crm.leads.view', lead.id)"
                                class="block min-w-0 flex-1 hover:bg-gray-50 transition-colors"
                            >
                                <div class="min-w-0">
                                    <p class="font-medium text-gray-900 mb-1">
                                        {{ lead.name }}
                                    </p>
                                    <p
                                        v-if="lead.title"
                                        class="text-xxs text-gray-500 mb-0"
                                    >
                                        {{ lead.title }}
                                    </p>
                                    <p class="text-xxs text-gray-500 mb-0">
                                        {{
                                            lead.email ||
                                            lead.phone ||
                                            _mft(
                                                'crm:leadCompany.noContactDetails'
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="
                                            lead.note_count > 0 ||
                                            lead.deal_count > 0 ||
                                            lead.pending_action_count > 0
                                        "
                                        class="text-xxs text-gray-500 mb-0 space-x-2 mt-1"
                                    >
                                        <pill
                                            v-if="lead.note_count > 0"
                                            size="xs"
                                        >{{ lead.note_count }} notes</pill>
                                        <pill
                                            v-if="lead.deal_count > 0"
                                            size="xs"
                                        >{{ lead.deal_count }} deals</pill>
                                        <pill
                                            v-if="lead.pending_action_count > 0"
                                            size="xs"
                                        >{{ lead.pending_action_count }} actions</pill>
                                    </p>
                                </div>
                            </a>

                            <button-with-confirmation
                                size="sm"
                                style-type="danger"
                                confirm-type="danger"
                                :state="disconnectButtonState(lead)"
                                :title="_mft('crm:leadCompany.disconnectContact')"
                                class="opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                                :confirm-text="_mft('crm:leadCompany.disconnectContactConfirm', { name: lead.name })"
                                :confirm-button-label="_mft('crm:leadCompany.disconnectContact')"
                                @confirmed="disconnectLead(lead)"
                            >
                                <i class="fa-light fa-link-slash"></i>
                            </button-with-confirmation>

                            <span
                                v-if="leadCompany.primary_lead_id === lead.id"
                                class="rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700 absolute -top-6 -right-6"
                            >
                                {{ _mft('crm:leadCompany.primaryBadge') }}
                            </span>
                        </div>
                    </card-sub>
                </div>
            </div>
        </div>

        <modal-basic :open="deleteDialogOpen" @closed="closeDeleteDialog">
            <template #header>
                {{ _mft('crm:leadCompany.deleteCompany') }}
            </template>
            <template #body>
                <div class="space-y-4">
                    <p class="text-sm text-gray-700">
                        {{ _mft('crm:leadCompany.deleteCompanyConfirm') }}
                    </p>
                    <p
                        v-if="deleteError"
                        role="alert"
                        class="text-sm text-red-700"
                    >
                        {{ deleteError }}
                    </p>
                    <div class="flex justify-end space-x-2">
                        <button-secondary
                            :disabled="deleteState === 'waiting'"
                            @click="closeDeleteDialog"
                        >
                            {{ _mft('shared:action.cancel') }}
                        </button-secondary>
                        <button-base
                            style-type="danger"
                            :state="deleteState"
                            :disabled="deleteState === 'waiting'"
                            @click="deleteLeadCompany"
                        >
                            {{ _mft('crm:leadCompany.deleteCompany') }}
                        </button-base>
                    </div>
                </div>
            </template>
        </modal-basic>

        <slideout-with-slots
            v-if="addingContact"
            @close="addingContact = false"
        >
            <template #title>
                <h2>{{ _mft('crm:addContact') }}</h2>
            </template>
            <template #body>
                <form-lead-create
                    :lead-company="leadCompany"
                    :redirect-on-success="false"
                    @created="handleLeadCreated"
                />
            </template>
        </slideout-with-slots>
    </div>
</template>
